/**
 * THE VAT RETURN (tax.vat_return@1) — what the current version says, set the way a professional's VAT file is set.
 *
 * EVERY FIGURE IS THE SERVER'S. The engine computed every total; the work OS formatted every amount and wrote every
 * sentence (`work.vatReturn`). This file arranges them and computes nothing: no sum, no sign, no rate.
 *
 * THE ORDER IS THE READER'S. The return first (the lead schedule); then the work programme and where each step
 * stands; then the supporting schedules — sales tax, purchase tax, documents excluded, bank items not explained —
 * each row ticked to its source; then the VAT control account, the review points and the limitations.
 */
import type { VatReturnView } from "@leveri/contracts";
import { Figure, Label, Section, SectionHead, StateText, Statement, markerTone } from "../../file/index.js";

const SUMMARY = "cursor-pointer list-none text-[15px] font-semibold text-ink hover:underline";
const NUM = "text-right [font-variant-numeric:tabular-nums]";

/** 1 — the return: the answer, then its lines, the net position last. */
export function VatReturnLead({ vat }: { vat: VatReturnView }) {
  return (
    <Section id="figures">
      <Statement>{vat.headline}</Statement>
      <p className="mt-3 text-[13px] text-stone" data-testid="vat-verdict">
        <StateText tone={markerTone(vat.tone)}>{vat.verdictLabel}</StateText>
        {vat.periodText ? ` · ${vat.periodText}` : ""}
      </p>
      {vat.basisNote && <p className="mt-1 text-[13px] text-stone">{vat.basisNote}</p>}
      {vat.figures.length > 0 && (
        <div className="mt-5" data-testid="vat-return-figures">
          {/* A figure's note is a sentence, said under its line — never set as a source citation. */}
          {vat.figures.map((figure, index) => (
            <div key={figure.key}>
              <Figure first={index === 0} label={figure.label} value={figure.text} total={figure.total} />
              {figure.note && <p className="-mt-1 pb-2 text-[12px] text-stone">{figure.note}</p>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/** 2 — the work programme, in the order a professional performs it, and where each step stands. */
export function VatReturnPlan({ vat }: { vat: VatReturnView }) {
  if (vat.programme.length === 0) return null;
  return (
    <Section id="programme">
      <SectionHead>The work programme</SectionHead>
      <ol className="flex flex-col gap-3" data-testid="vat-programme">
        {vat.programme.map((step) => (
          <li key={step.step} className="grid grid-cols-[28px_1fr] gap-x-3">
            <span className="pt-0.5 font-mono text-[13px] text-stone">{step.step}</span>
            <div>
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="text-[15px] text-ink">{step.title}</span>
                <span className="text-[13px]"><StateText tone={markerTone(step.tone)}>{step.stateLabel}</StateText></span>
              </div>
              <p className="mt-0.5 text-[13px] text-stone">{step.note}</p>
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
}

/** 3 — the working: the supporting schedules, the control account, what is left for you, what the return cannot say. */
export function VatReturnWorking({ vat }: { vat: VatReturnView }) {
  return (
    <Section id="working">
      <SectionHead>The working</SectionHead>
      <div className="flex flex-col gap-5">
        {vat.schedules.map((schedule) => (
          <details key={schedule.key} open={schedule.key === "sales" || schedule.key === "purchases" ? undefined : schedule.rows.length > 0} data-testid={`vat-schedule-${schedule.key}`}>
            <summary className={SUMMARY}>
              {schedule.title} <span className="ml-1 text-[13px] font-normal text-stone">{schedule.rows.length}</span>
            </summary>
            {schedule.note && <p className="mt-1.5 text-[13px] text-stone">{schedule.note}</p>}
            {schedule.rows.length === 0 ? (
              <p className="mt-2 text-sm text-ink-soft">{schedule.empty}</p>
            ) : (
              // Wide on purpose: it scrolls inside its own box on a phone, never the page.
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[640px] text-[13px]">
                  <thead>
                    <tr className="border-b border-rule text-left text-stone">
                      {schedule.columns.map((column) => (
                        <th key={column.label} className={`py-2 pr-3 font-normal ${column.numeric ? NUM : ""}`}>{column.label}</th>
                      ))}
                      <th className="py-2 pr-3 font-normal">Source</th>
                      <th className="py-2 font-normal" />
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.rows.map((row, index) => (
                      <tr key={`${row.source}-${index}`} className="border-b border-rule-soft text-ink">
                        {row.cells.map((cell, i) => (
                          <td key={i} className={`py-2 pr-3 ${schedule.columns[i]?.numeric ? `whitespace-nowrap ${NUM}` : ""}`}>{cell}</td>
                        ))}
                        <td className="whitespace-nowrap py-2 pr-3 text-stone">{row.source}</td>
                        <td className="py-2 text-stone">{row.flag ?? ""}</td>
                      </tr>
                    ))}
                    {schedule.totals && (
                      <tr className="font-medium text-ink">
                        {schedule.totals.map((cell, i) => (
                          <td key={i} className={`py-2 pr-3 ${schedule.columns[i]?.numeric ? `whitespace-nowrap ${NUM}` : ""}`}>{cell}</td>
                        ))}
                        <td /><td />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </details>
        ))}

        {vat.controlAccount && (
          <div data-testid="vat-control-account">
            <Label>VAT control account</Label>
            <p className="mt-1 text-[15px]"><StateText tone={markerTone(vat.controlAccount.tone)}>{vat.controlAccount.stateLabel}</StateText></p>
            <p className="mt-1 text-sm text-ink-soft">{vat.controlAccount.sentence}</p>
            <div className="mt-3">
              {vat.controlAccount.lines.map((line, index) => (
                <Figure key={line.label} first={index === 0} label={line.label} value={line.text} total={line.total} />
              ))}
            </div>
          </div>
        )}

        {vat.reviewerItems.length > 0 && (
          <div data-testid="vat-reviewer-items">
            <Label>For you to decide</Label>
            <ul className="mt-1.5 flex list-disc flex-col gap-1 pl-5 text-sm text-ink">
              {vat.reviewerItems.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        )}
        {vat.limitations.length > 0 && (
          <div>
            <Label>What this return does not cover</Label>
            <ul className="mt-1.5 flex list-disc flex-col gap-1 pl-5 text-[13px] text-stone">
              {vat.limitations.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        )}
        <p className="text-[13px] text-stone">This return is a draft for your review. Leveri files nothing with any tax authority.</p>
      </div>
    </Section>
  );
}
